import { Component, type ErrorInfo, type ReactNode } from 'react'

export interface ErrorBoundaryProps {
  children: ReactNode
  /** Names the region in the default fallback ("Chart unavailable"). */
  label?: string
  /** Custom fallback; receives a reset callback. */
  fallback?: (reset: () => void) => ReactNode
  /** When any value changes, a tripped boundary resets (e.g. the route path). */
  resetKeys?: ReadonlyArray<unknown>
  className?: string
}

type State = { failed: boolean }

function sameKeys(a: ReadonlyArray<unknown> = [], b: ReadonlyArray<unknown> = []): boolean {
  return a.length === b.length && a.every((v, i) => Object.is(v, b[i]))
}

/**
 * Catches render errors in its subtree and shows an "unavailable" fallback, so
 * one broken pane never blanks the page (spec 069 P61). Lives in `src/ui/`, so
 * it knows nothing about routing: callers pass `resetKeys`.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, State> {
  state: State = { failed: false }

  static getDerivedStateFromError(): State {
    return { failed: true }
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    console.error('ErrorBoundary caught', error, info.componentStack)
  }

  componentDidUpdate(prev: ErrorBoundaryProps): void {
    if (this.state.failed && !sameKeys(prev.resetKeys, this.props.resetKeys)) {
      this.reset()
    }
  }

  reset = (): void => {
    this.setState({ failed: false })
  }

  render(): ReactNode {
    if (!this.state.failed) return this.props.children
    if (this.props.fallback) return this.props.fallback(this.reset)
    const label = this.props.label ?? 'This section'
    return (
      <div
        role="alert"
        data-error-boundary=""
        className={
          this.props.className ??
          'flex h-full w-full flex-col items-center justify-center gap-3 p-6 font-mono text-[12px] text-paper-50'
        }
      >
        <span>{label} unavailable</span>
        <button type="button" onClick={this.reset} className="border border-current/25 px-3 py-1 text-[11px] uppercase">
          Try again
        </button>
      </div>
    )
  }
}
